import { sendLog } from "./api/logger-api";
import { getIsEdge } from "./configs/env";

export interface LogPayload {
  portal_alias?: string | null;
  document_name?: string | null;
  search_query?: string | null;
  stacktrace?: string | null;
  message?: string | null;
  error?: unknown;
  href?: string | null;
  // Workspace and portal identifiers
  workspace_id?: string | null;
  portal_id?: string | null;
  workspace_alias?: string | null;
  // Document and content identifiers
  kapp_id?: string | null;
  doc_guid?: string | null;
  guid?: string | null;
  page?: string | null;
  // User and session identifiers
  user_id?: string | null;
  session_id?: string | null;
  // Router and navigation
  router_props?: unknown;
  path?: string | null;
  // URL and API related
  alias_url?: string | null;
  auth_url?: string | null;
  url?: string | null;
  home_url?: string | null;
  video_section_url?: string | null;
  video_source_url?: string | null;
  video_text_url?: string | null;
  // Authentication and initialization
  user?: unknown;
  // Events and responses
  event_type?: string | null;
  error_detail?: string | null;
  ping_response?: unknown;
  // Document and file handling
  document_uri?: string | null;
  file_name?: string | null;
  element_id?: string | null;
  // TTS and speech synthesis
  error_type?: string | null;
  text?: string | null;
  voice?: string | null;
  lang?: string | null;
  cancel_reason?: unknown;
  // Categories and search
  categories?: unknown;
  // Network request fields
  api_endpoint?: string | null;
  request_method?: string | null;
  request_headers?: Record<string, string> | null;
  request_body?: any;
  base_url?: string | null;
  full_url?: string | null;

  // Network response fields
  response_status?: number | null;
  response_headers?: Record<string, string> | null;
  response_body?: any;
  api_response_time?: number | null;

  // EventSource fields
  message_count?: number | null;
  total_data_size?: number | null;
  event_detail?: string | null;

  // Additional context fields
  [key: string]: unknown;
}

const REDACTED = "[REDACTED]";

// One list, used for object keys and for keys inside serialized bodies, so a
// credential is redacted the same way whether it arrives as an object or as a
// string. `code` is exact-match only: substring matching would eat
// country_code, error_code, barcode and friends.
const SECRET_WORDS =
  "authorization|cookie|set-cookie|bearer|token|jwt|password|pwd|secret|credentials|api[-_]?key|client_secret";
const EXACT_SECRET_WORDS = "auth|code|auth_code|authorization_code|sid";

const SECRET_KEY = new RegExp(
  `^(?:${SECRET_WORDS}|${EXACT_SECRET_WORDS})$|${SECRET_WORDS}`,
  "i",
);
// token=... in a url or hash fragment
const SECRET_IN_URL = new RegExp(
  `((?:^|[?&#])(?:[\\w-]*(?:${SECRET_WORDS})[\\w-]*|${EXACT_SECRET_WORDS})=)[^&#\\s"']+`,
  "gi",
);
// "token": "..." inside a serialized body. fetch bodies, and bodies that
// network-logger truncated, reach the logger as strings, so key-based
// redaction never sees them. The value pattern follows the JSON string
// grammar so an escaped quote inside a secret does not end the match early,
// and the quotes may themselves be backslash-escaped, which is how a body that
// was serialized twice arrives.
const SECRET_IN_JSON = new RegExp(
  `(\\\\?"(?:[\\w-]*(?:${SECRET_WORDS})[\\w-]*|${EXACT_SECRET_WORDS})\\\\?"\\s*:\\s*\\\\?")(?:\\\\.|[^"\\\\])*`,
  "gi",
);

// Key matching cannot see a bare JWT under a neutral key, an `Authorization:
// Bearer ...` string, or a doubly-serialized body. Match the shape too.
const JWT_SHAPE = /\beyJ[\w-]{8,}\.[\w-]+\.[\w-]+/g;
const AUTH_SCHEME = /\b(Basic|Bearer)\s+[\w+/=.~-]+/gi;

// A length or a boolean derived from a secret is the non-reversible substitute
// we ask callers to log, so it must survive redaction.
const SAFE_SUFFIX = /_(length|len|count|match|present|set)$|^passwords_/i;
const isSecretKey = (key: string): boolean =>
  !SAFE_SUFFIX.test(key) && SECRET_KEY.test(key);

const redactString = (value: string): string =>
  value
    .replace(SECRET_IN_URL, `$1${REDACTED}`)
    .replace(SECRET_IN_JSON, `$1${REDACTED}`)
    .replace(JWT_SHAPE, REDACTED)
    .replace(AUTH_SCHEME, `$1 ${REDACTED}`);

// Bounds the work per log call. redactSecrets walks a graph, and it runs on
// every network request, so an unbounded walk can block the main thread.
const MAX_NODES = 5000;

/**
 * Deep-copy `value` with every credential-bearing field replaced by [REDACTED].
 * Runs at the one point every log payload passes through, so no call site can
 * leak a JWT, cookie, SAS token or password to the console or the logger backend.
 * `seen` tracks the current ancestor chain only, so a repeated sibling is copied
 * rather than mislabelled as circular.
 */
export const redactSecrets = (
  value: unknown,
  seen = new WeakSet(),
  budget = { left: MAX_NODES },
): unknown => {
  if (typeof value === "string") return redactString(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return "[CIRCULAR]";
  if (budget.left-- <= 0) return "[TRUNCATED]";
  seen.add(value);

  try {
    if (Array.isArray(value)) {
      return value.map((v) => redactSecrets(v, seen, budget));
    }
    // Errors, incl. AxiosError whose toJSON exposes the request headers
    if (value instanceof Error) {
      return {
        name: value.name,
        message: redactString(value.message),
        stack: value.stack ? redactString(value.stack) : value.stack,
      };
    }

    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = isSecretKey(key) ? REDACTED : redactSecrets(val, seen, budget);
    }
    return out;
  } finally {
    seen.delete(value);
  }
};

// Logging must never break the caller, and it runs on every network request.
// The fallback is the path where redaction already failed, so it still redacts.
const safeRedact = (payload: LogPayload): LogPayload => {
  try {
    return redactSecrets(payload) as LogPayload;
  } catch {
    return {
      message: payload.message
        ? redactString(String(payload.message))
        : payload.message,
      event_type: payload.event_type,
    };
  }
};

class Logger {
  private async sendToAPI(
    level: "info" | "warning" | "error",
    payload: LogPayload,
  ) {
    try {
      const loggerEnabled = (global as any)._loggerEnabled;
      if (loggerEnabled === false) {
        return;
      }

      const isEdge = getIsEdge();
      const token = (global as any).token;
      if (!token && !isEdge) {
        console.warn(
          "Logger: No token available, skipping log send. Logger is enabled but logs cannot be sent without authentication.",
        );
        // For non-edge, require token. For edge, backend doesn't check auth.
        return;
      }

      const dataWithHref = {
        ...payload,
        source: "web-app",
        href: redactString(String(payload.href || window.location.href)),
        timestamp: Date.now(),
      };

      // Use token if available, otherwise placeholder for edge
      await sendLog(level, dataWithHref, token || "edge-client");
    } catch (error) {
      //TODO: store these in localStorage and sync later

      // the AxiosError carries config.headers with the bearer token
      console.error(
        "Logger: Failed to send log to API:",
        error instanceof Error ? error.message : error,
      );
    }
  }

  private formatConsoleMessage(level: string, payload: LogPayload): string {
    const timestamp = new Date().toISOString();
    const href = redactString(String(payload.href || window.location.href));

    let message = `[${timestamp}] [${level.toUpperCase()}]`;

    if (payload.message) {
      message += ` ${payload.message}`;
    }

    if (payload.portal_alias) {
      message += ` (portal_alias: ${payload.portal_alias})`;
    }

    if (payload.document_name) {
      message += ` (document_name: ${payload.document_name})`;
    }

    if (payload.search_query) {
      message += ` (search_query: ${payload.search_query})`;
    }

    message += ` (href: ${href})`;

    return message;
  }

  log(payload: LogPayload) {
    const safe = safeRedact(payload);
    console.log(this.formatConsoleMessage("info", safe), safe);

    this.sendToAPI("info", safe);
  }

  warn(payload: LogPayload) {
    const safe = safeRedact(payload);
    console.warn(this.formatConsoleMessage("warning", safe), safe);

    this.sendToAPI("warning", safe);
  }

  error(payload: LogPayload) {
    if (
      payload.error &&
      payload.error instanceof Error &&
      !payload.stacktrace
    ) {
      payload.stacktrace = payload.error.stack;
    }

    const safe = safeRedact(payload);
    console.error(this.formatConsoleMessage("error", safe), safe);

    this.sendToAPI("error", safe);
  }
}

const logger = new Logger();

export default logger;
